import React from "react";
import { Navigate } from "react-router-dom";
import permissionsService from "../../services/permissionsService";

interface PermissionGuardProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  /**
   * Restrict the route to the CertainPath company.
   *
   * Gates on the ACTIVE company, so a coach switched into a client loses
   * access until they switch back (D22, reversing D5). That is deliberate:
   * the screens behind this flag are cross-tenant views, and rendering one
   * while the user is scoped into a single client company is a context
   * mismatch — the data on screen is not the data the container claims.
   * Nothing behind it reads the active company for scoping, so being
   * switched in buys the user nothing to begin with.
   *
   * There is deliberately no client-side super-admin bypass in this app —
   * super-admin is expressed purely by the backend granting permissions.
   */
  requireCertainPathCompany?: boolean;
}

export default function PermissionGuard({
  children,
  requiredPermissions = [],
  requireCertainPathCompany = false,
}: PermissionGuardProps) {
  const { hasPermission, hasCertainPathCompany } = permissionsService();

  const permissionsPass =
    requiredPermissions.length === 0 ||
    requiredPermissions.some((perm) => hasPermission(perm));

  const certainPathPass = !requireCertainPathCompany || hasCertainPathCompany();

  if (!permissionsPass || !certainPathPass) {
    return <Navigate replace to="/403" />;
  }

  return <>{children}</>;
}
